/* ============================================================================
   LIBRO DE VUELO DEL PILOTO

   La vista que faltaba. El dominio ya estaba: cada cierre de cronómetro deja
   un asiento con el **flight time** —el número del piloto— y esta pantalla lo
   muestra como el libro en papel, con los totales que un piloto conoce de
   memoria.

   ══════════════════════════════════════════════════════════════════════════
   DOS COSAS QUE ESTA PANTALLA NO PUEDE HACER

   1. **No afirma que el piloto esté habilitado.** Cuenta aterrizajes y los
      compara contra un umbral CONFIGURABLE. Mientras ese umbral no esté
      verificado contra la reglamentación del operador, la pantalla muestra el
      conteo y dice que el umbral no está confirmado — nunca un «corriente» a
      secas.

      Un piloto que cree estar habilitado y no lo está vuela igual.

   2. **No esconde los asientos sin fecha.** No entran en ningún período, así
      que el total del libro no cuadraría con la suma de los meses. Se cuentan
      aparte para que alguien los arregle.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import React, { useMemo, useState } from "react";

import {
  periodosDeLibro, currencyDePiloto, porMes, totalesDeLibro,
  aHhMm, FUNCIONES, CONDICIONES, CURRENCY_POR_DEFECTO, PERIODOS,
  asientoManual, asientoDeArrastre, etiquetaDeEntrada, esVuelo, deHhMm,
  COLUMNAS_LIBRO, GRUPOS_LIBRO, COLUMNAS_HORAS, arrastresDeLibro,
  claveDeAsiento, corregirAsiento, quitarAsiento, fueCorregido, aHhMm as aHhMmDe,
  columnasRetiradasConDatos,
} from "../../domain/bitacoraPiloto.js";
/* ═══ EL AVISO DE RESPALDO SE MUESTRA ACÁ ═══

   `respaldoLibro.js` se escribió en la e115 con sus pruebas en verde y
   **nadie lo llamaba**: el aviso de los 7 días no aparecía, la alarma de los
   14 no sonaba, y el libro —que es el respaldo documental de una licencia—
   vivía solo en el IndexedDB del dispositivo sin que nada lo recordara.

   Va en esta pantalla y no en el cascarón porque es acá donde está el botón
   que resuelve el problema: un aviso que manda a buscar el remedio a otra
   pantalla se pospone. */
import {
  paqueteDeRespaldo, planillaDelLibro, TEXTOS,
  hayQueAvisar, registrarRespaldo, posponer,
} from "../../domain/respaldoLibro.js";

/* La planilla para archivar e imprimir. El dominio devuelve los DATOS de la
   planilla —columnas, filas, totales—; convertirla en un archivo Excel es de
   la pantalla, que es la que tiene el navegador. */
import * as XLSX from "xlsx";
import "./libroDeVuelo.css";

const etiquetaFuncion = (clave) => FUNCIONES.find(([c]) => c === clave)?.[1] || clave;

export default function LibroDeVuelo({
  /* Todos los asientos de todos los pilotos: el libro filtra el suyo. */
  todos = null,
  pilotos = [],
  pilotoPorDefecto = null,
  onNuevoPiloto,
  /* Compatibilidad con el modo anterior: una lista ya filtrada. */
  asientos: asientosProp = [],
  pilotoNombre = "",
  pilotId: pilotIdProp = null,
  umbralesCurrency = CURRENCY_POR_DEFECTO,
  hasta = null,
  onAgregar,
  onReemplazarTodos,
}) {
  /* ═══ CADA PILOTO SU LIBRO ═══

     El piloto del libro es una elección propia, no la del vuelo cargado. Son
     dos cosas distintas: quién vuela hoy, y de quién es el libro que estoy
     llenando.

     Se recuerda entre sesiones: el que llena su libro vuelve a llenarlo. */
  const [pilotoElegido, setPilotoElegido] = useState(() => {
    try {
      const guardado = localStorage.getItem("hcLibroPiloto");
      if (guardado && pilotos.some((p) => p.id === guardado)) return guardado;
    } catch { /* modo privado */ }
    return pilotoPorDefecto || pilotos[0]?.id || null;
  });

  const elegirPiloto = (id) => {
    setPilotoElegido(id);
    try { localStorage.setItem("hcLibroPiloto", id); } catch { /* modo privado */ }
  };

  const conRegistro = Array.isArray(todos);
  const pilotoActual = pilotos.find((p) => p.id === pilotoElegido) || null;
  const pilotId = conRegistro ? pilotoElegido : pilotIdProp;

  /* Los asientos SIN piloto se muestran siempre: si no, un vuelo cerrado sin
     piloto asignado queda guardado y no aparece en el libro de nadie. */
  const asientos = useMemo(() => (
    conRegistro
      ? todos.filter((a) => !a?.pilotId || a.pilotId === pilotoElegido)
      : asientosProp
  ), [conRegistro, todos, asientosProp, pilotoElegido]);

  const nombreMostrado = pilotoActual
    ? `${pilotoActual.nombre} ${pilotoActual.apellido || ""}`.trim()
    : pilotoNombre;
  const [mesAbierto, setMesAbierto] = useState(null);
  const [formulario, setFormulario] = useState(null);
  const [editando, setEditando] = useState(null);
  const [porQuitar, setPorQuitar] = useState(null);

  const periodos = useMemo(() => periodosDeLibro(asientos, hasta), [asientos, hasta]);
  const currency = useMemo(
    () => currencyDePiloto(asientos, hasta, umbralesCurrency),
    [asientos, hasta, umbralesCurrency],
  );
  const meses = useMemo(() => porMes(asientos), [asientos]);
  const arrastres = useMemo(() => arrastresDeLibro(asientos), [asientos]);

  /* Las columnas que salieron del libro pero tienen horas cargadas: se
     muestran igual. Una columna guardada que no se ve en ningún lado es una
     hora perdida que nadie puede cotejar. */
  const retiradas = useMemo(() => columnasRetiradasConDatos(asientos), [asientos]);
  const totalGeneral = useMemo(() => totalesDeLibro(asientos, {}), [asientos]);

  /* ═══ RESPALDO ═══

     El contador sube al exportar y al posponer: sin él, el aviso seguiría en
     pantalla después de exportar y el piloto creería que no se guardó.

     Los hooks van ANTES de la salida temprana del libro vacío. React exige que
     todos se llamen en el mismo orden en cada dibujado, y saltearlos al pasar
     de un libro vacío a uno con asientos deja la pantalla en blanco sin
     explicación — es el error de la e105. */
  const [respaldoTic, setRespaldoTic] = useState(0);
  const avisoRespaldo = useMemo(
    () => hayQueAvisar({ asientos: asientos.length }),
    /* El lint la marca como innecesaria porque no aparece adentro, y se
       equivoca: la respuesta no sale de estas variables sino del
       almacenamiento, que cambia al exportar y al posponer. Sin esta
       dependencia el aviso seguiría en pantalla después de exportar, y el
       piloto creería que no se guardó. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [asientos.length, respaldoTic],
  );

  const SelectorPiloto = () => (conRegistro ? (
    <div className="libro__piloto">
      <label className="libro__campo">
        <span>Libro de</span>
        <select value={pilotoElegido || ""} onChange={(e) => elegirPiloto(e.target.value)}>
          {pilotos.map((p) => (
            <option key={p.id} value={p.id}>
              {`${p.nombre} ${p.apellido || ""}`.trim()}
            </option>
          ))}
        </select>
      </label>
      {onNuevoPiloto && (
        <button type="button" className="libro__paso" onClick={onNuevoPiloto}>
          + Piloto
        </button>
      )}
      {/* Los vuelos sin piloto asignado aparecen en todos los libros hasta que
          alguien los adjudique: es preferible verlos de más que perderlos. */}
      {conRegistro && todos.some((a) => !a?.pilotId) && (
        <p className="libro__nota">
          Hay {todos.filter((a) => !a?.pilotId).length} vuelos sin piloto asignado.
          Se muestran en todos los libros hasta que se les asigne uno.
        </p>
      )}
    </div>
  ) : null);

  /* Bajar un archivo es lo unico del respaldo que no puede hacer el dominio:
     necesita el navegador. */
  const bajarArchivo = (nombre, contenido, tipo) => {
    const blob = new Blob([contenido], { type: tipo });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportar = () => {
    /* El paquete y el nombre los define el DOMINIO: es el mismo respaldo que
       se restaura y el mismo que se presenta ante una autoridad. Antes esta
       pantalla armaba su propio objeto, con su propio formato y su propio
       nombre, mientras el modulo que sabia cuando insistir no lo llamaba
       nadie. */
    const paquete = paqueteDeRespaldo({
      asientos, piloto: nombreMostrado || "", totales: totalGeneral, periodos, currency,
    });
    bajarArchivo(paquete.nombre, paquete.texto, paquete.tipo);

    /* ═══ SE REGISTRA QUE SE EXPORTÓ, NO QUE SE RESPALDÓ ═══

       La aplicación no sabe si el archivo llegó a algún lado: lo guarda el
       piloto donde quiera. Decir «respaldado» sería afirmar algo que no se
       puede saber. */
    registrarRespaldo();
    setRespaldoTic((n) => n + 1);
  };

  /**
   * La planilla, para archivar y para imprimir.
   *
   * Es lo que se firma y se guarda en papel: un respaldo que solo se puede
   * leer en la aplicación que lo generó no sirve como respaldo documental.
   * Las columnas las define el libro, así que una columna nueva aparece sola.
   */
  const exportarPlanilla = () => {
    const planilla = planillaDelLibro({
      asientos, piloto: nombreMostrado || "", totales: totalGeneral,
    });

    const filas = [
      [planilla.encabezado.titulo],
      [`Generado: ${planilla.encabezado.generado}`],
      [],
      planilla.encabezados,
      ...planilla.filas,
    ];
    if (planilla.filaTotales) filas.push(planilla.filaTotales);
    filas.push([], [planilla.encabezado.aviso]);

    const hoja = XLSX.utils.aoa_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Libro");
    XLSX.writeFile(libro, planilla.nombre);
    registrarRespaldo();
    setRespaldoTic((n) => n + 1);
  };

  if (asientos.length === 0) {
    return (
      <section className="libro">
        <SelectorPiloto />
        <p className="libro__vacio">
          Todavía no hay vuelos registrados. Se cargan solos al cerrar el
          cronómetro en el modo vuelo, y también puedes cargarlos a mano: el
          histórico anterior, o un vuelo que no se cronometró.
        </p>

        {/* El libro vacío OFRECE cargar. Antes solo explicaba de dónde
            vendrían los datos, y un piloto con años de vuelo no tenía por
            dónde empezar su libro electrónico. */}
        {onAgregar && (
          <div className="libro__acciones">
            <button type="button" className="libro__exportar" onClick={() => setFormulario("vuelo")}>
              Cargar un vuelo
            </button>
            <button type="button" className="libro__paso" onClick={() => setFormulario("arrastre")}>
              Cargar horas anteriores
            </button>
          </div>
        )}

        {formulario && (
          <FormularioLibro
            tipo={formulario}
            pilotId={pilotId}
            onGuardar={(a) => { onAgregar(a); setFormulario(null); }}
            onCancelar={() => setFormulario(null)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="libro">
      <SelectorPiloto />

      {/* ═══ EL LIBRO VIVE EN ESTE DISPOSITIVO ═══

          Un piloto que pierde el iPad pierde su libro de vuelo, y un libro de
          vuelo es el respaldo documental de una licencia.

          A los 14 días el aviso es ALARMA y NO SE PUEDE POSPONER: correrlo
          tres días más es exactamente lo que hace que un libro se pierda. Y
          no tiene «×»: un aviso con cruz se cierra por reflejo sin leerlo. */}
      {avisoRespaldo.mostrar && (
        <div
          className={`libro__respaldo libro__respaldo--${avisoRespaldo.estado.nivel}`}
          role="alert"
        >
          <strong>
            {avisoRespaldo.estado.nivel === "alarma"
              ? "El libro no está respaldado"
              : "Conviene exportar el libro"}
          </strong>
          <span>{avisoRespaldo.estado.motivo}</span>
          {/* No es copia en la nube: lo que se registra es que se exportó. */}
          <span className="libro__respaldo-nota">
            Exportar baja un archivo a este dispositivo. Guardalo fuera de él
            —correo, disco o carpeta compartida—: la aplicación no puede saber
            si llegó a algún lado.
          </span>
          {avisoRespaldo.veces > 0 && (
            <span className="libro__respaldo-nota">
              Pospuesto {avisoRespaldo.veces} {avisoRespaldo.veces === 1 ? "vez" : "veces"}.
            </span>
          )}
          <div className="libro__respaldo-acciones">
            <button type="button" className="libro__exportar" onClick={exportar}>
              Exportar ahora
            </button>
            {avisoRespaldo.sePuedePosponer && (
              <button
                type="button"
                className="libro__paso"
                onClick={() => { posponer(); setRespaldoTic((n) => n + 1); }}
              >
                Posponer 3 días
              </button>
            )}
          </div>
        </div>
      )}

      <header className="libro__cabecera">
        <h3 className="libro__titulo">
          Libro de vuelo{nombreMostrado ? ` · ${nombreMostrado}` : ""}
        </h3>
        {onAgregar && (
          <button type="button" className="libro__paso" onClick={() => setFormulario("vuelo")}>
            + Vuelo
          </button>
        )}
        {onAgregar && (
          <button type="button" className="libro__paso" onClick={() => setFormulario("arrastre")}>
            + Horas anteriores
          </button>
        )}
        <button type="button" className="libro__exportar" onClick={exportar}>
          {TEXTOS.accion}
        </button>
        {/* La planilla es para archivar y firmar: el libro en papel. */}
        <button type="button" className="libro__paso" onClick={exportarPlanilla}>
          {TEXTOS.accionPlanilla}
        </button>
      </header>

      {formulario && (
        <FormularioLibro
          tipo={formulario}
          pilotId={pilotId}
          onGuardar={(a) => { onAgregar(a); setFormulario(null); }}
          onCancelar={() => setFormulario(null)}
        />
      )}

      {editando && (
        <FormularioLibro
          tipo={esVuelo(editando) ? "vuelo" : "arrastre"}
          pilotId={pilotId}
          inicial={editando}
          onGuardarDatos={(datos) => {
            const r = corregirAsiento(todos || asientos, claveDeAsiento(editando), datos);
            if (r.errores.length === 0) { onReemplazarTodos(r.lista); setEditando(null); }
            return r;
          }}
          onCancelar={() => setEditando(null)}
        />
      )}

      {/* Quitar pide confirmación: un arrastre borrado por error se lleva miles
          de horas, y volver a cargarlo es transcribir todo de nuevo. */}
      {porQuitar && (
        <div className="libro__formulario" role="alertdialog">
          <h4 className="libro__form-titulo">Quitar del libro</h4>
          <p className="libro__nota">
            {/* El texto distingue un vuelo de un arrastre: quitar un vuelo
                borra un día de trabajo; quitar un arrastre, miles de horas.
                No se pueden confirmar con el mismo mensaje. */}
            <strong>
              {esVuelo(porQuitar)
                ? `Vuelo del ${porQuitar.fecha}${porQuitar.matricula ? ` · ${porQuitar.matricula}` : ""}`
                : (porQuitar.libro || "Libro anterior")}
            </strong>
            {esVuelo(porQuitar) ? "" : `, hasta ${porQuitar.fecha}`},
            con {porQuitar.flightTimeHhMm || aHhMmDe(porQuitar.flightTime || 0)} de total.
            Se quita del libro y deja de sumar.
          </p>
          <div className="libro__form-botones">
            <button type="button" className="libro__paso" onClick={() => setPorQuitar(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="libro__quitar-confirmar"
              onClick={() => {
                const r = quitarAsiento(todos || asientos, claveDeAsiento(porQuitar));
                if (r.errores.length === 0) onReemplazarTodos(r.lista);
                setPorQuitar(null);
              }}
            >
              Quitar
            </button>
          </div>
        </div>
      )}

      {/* Los totales que un piloto conoce de memoria. */}
      <div className="libro__totales">
        {PERIODOS.map((p) => (
          <div className="libro__total" key={p.clave}>
            <span>{p.etiqueta}</span>
            <strong>{periodos[p.clave]?.totalHhMm ?? "—"}</strong>
            <small>{periodos[p.clave]?.vuelos ?? 0} vuelos</small>
          </div>
        ))}
        <div className="libro__total libro__total--grande">
          <span>Total</span>
          <strong>{totalGeneral.totalHhMm}</strong>
          <small>{totalGeneral.vuelos} vuelos</small>
        </div>
      </div>

      <Currency currency={currency} />

      {/* TOTALES POR COLUMNA, como el pie del libro de papel.

          El bloque de arriba da el total y los períodos; este da el desglose
          que se mira para una licencia: cuánto como PIC, cuánto de
          instrumentos, cuántos aterrizajes nocturnos. */}
      <div className="libro__desglose">
        <span className="libro__desglose-titulo">Totales por columna</span>
        <div className="libro__desglose-campos">
          {COLUMNAS_LIBRO
            .filter((c) => c.tipo !== "texto" && c.clave !== "flightTime" && c.clave !== "catHelicoptero")
            .concat(retiradas)
            .map((c) => (
              /* La unidad al lado del número: «27987» y «00:00» en columnas
                 vecinas se leen como si fueran lo mismo. */
              <div className="libro__desglose-item" key={c.clave}>
                <span>{c.etiqueta}</span>
                <strong>
                  {c.tipo === "horas"
                    ? (totalGeneral.enHhMm?.[c.clave] ?? "00:00")
                    : (totalGeneral[c.clave] ?? 0)}
                  <em className="libro__unidad">{c.tipo === "horas" ? "h" : "ldg"}</em>
                </strong>
              </div>
            ))}
        </div>
      </div>

      {/* Horas por función a bordo: la experiencia como PIC no es la misma que
          como instructor, y el libro las separa. */}
      <div className="libro__funciones">
        {FUNCIONES.map(([clave]) => {
          const h = totalGeneral.porFuncion?.[clave] ?? 0;
          if (h === 0) return null;
          return (
            <span className="libro__funcion" key={clave}>
              {etiquetaFuncion(clave)}: <strong>{aHhMm(h)}</strong>
            </span>
          );
        })}
      </div>

      {totalGeneral.sinFecha > 0 && (
        <p className="libro__aviso" role="alert">
          {totalGeneral.sinFecha} {totalGeneral.sinFecha === 1 ? "asiento" : "asientos"} sin
          fecha. No entran en ningún período, así que los totales por mes no van a
          sumar el total general hasta que se les ponga fecha.
        </p>
      )}

      {/* LOS ARRASTRES VAN APARTE, como el pie del libro de papel:
          «TOTAL LIBRO ECUADOR», «TOTAL LIBRO BURKINA». Son saldos de otros
          libros, no meses de este. */}
      {arrastres.length > 0 && (
        <div className="libro__arrastres">
          <span className="libro__arrastres-titulo">Libros anteriores</span>
          {/* TODAS las columnas del libro, no una selección.

              Mostraba solo PIC, SIC e instrumentos: el resto del arrastre
              —cross country, noche, simulador, aproximaciones— quedaba
              guardado y sin verse, así que el piloto no podía comprobar que
              hubiera cargado bien su libro anterior. */}
          <div className="libro__tabla-ancha">
            <table className="libro__tabla">
              <thead>
                <tr>
                  <th>Libro</th><th>Hasta</th>
                  {COLUMNAS_LIBRO.filter((c) => c.tipo !== "texto" && c.clave !== "catHelicoptero")
                    .map((c) => (
                      <th key={c.clave}>
                        {c.etiqueta}
                        <span className="libro__unidad"> {c.tipo === "horas" ? "(h)" : "(n.º)"}</span>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {arrastres.map((a) => (
                  <tr key={`${a.libro}-${a.fecha}`}>
                    <td>
                      {a.libro || "Libro anterior"}
                      {/* Un asiento corregido lo dice: en el libro de papel se
                          tacha y se anota al margen. */}
                      {fueCorregido(a) && <span className="libro__entrada">corregido</span>}
                    </td>
                    <td>{a.fecha}</td>
                    {COLUMNAS_LIBRO.filter((c) => c.tipo !== "texto" && c.clave !== "catHelicoptero")
                      .map((c) => (
                        <td className="libro__num" key={c.clave}>
                          {c.tipo === "horas" ? aHhMm(a[c.clave] || 0) : (a[c.clave] || 0)}
                        </td>
                      ))}
                    {/* CORREGIR Y QUITAR.

                        Faltaban, y el hueco se notó al primer uso real: un
                        arrastre cargado con columnas en cero solo se podía
                        volver a cargar, duplicando el total. */}
                    {onReemplazarTodos && (
                      <td className="libro__acciones-fila">
                        <button
                          type="button"
                          className="libro__paso"
                          onClick={() => setEditando(a)}
                        >
                          Corregir
                        </button>
                        <button
                          type="button"
                          className="libro__quitar"
                          onClick={() => setPorQuitar(a)}
                          aria-label={`Quitar ${a.libro || "libro anterior"}`}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {arrastres.length > 1 && (
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total de libros anteriores</td>
                    {COLUMNAS_LIBRO.filter((c) => c.tipo !== "texto" && c.clave !== "catHelicoptero")
                      .map((c) => {
                        const suma = arrastres.reduce((x, a) => x + (Number(a[c.clave]) || 0), 0);
                        return (
                          <td className="libro__num" key={c.clave}>
                            {c.tipo === "horas" ? aHhMm(suma) : suma}
                          </td>
                        );
                      })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Una hoja por mes, como el libro en papel. */}
      <ul className="libro__meses">
        {meses.map((m) => (
          <li className="libro__mes" key={m.mes}>
            <button
              type="button"
              className="libro__mes-cabecera"
              onClick={() => setMesAbierto((x) => (x === m.mes ? null : m.mes))}
              aria-expanded={mesAbierto === m.mes}
            >
              <span className="libro__mes-nombre">{m.mes}</span>
              <span className="libro__mes-total">{m.totales.totalHhMm}</span>
              <span className="libro__mes-vuelos">{m.totales.vuelos} vuelos</span>
            </button>

            {mesAbierto === m.mes && (
              <table className="libro__tabla">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Aeronave</th><th>Matrícula</th>
                    <th>Ruta</th><th>Duración</th><th>Función</th>
                    <th>Ldg D</th><th>Ldg N</th>
                    {onReemplazarTodos && <th>&nbsp;</th>}
                  </tr>
                </thead>
                <tbody>
                  {m.asientos.map((a) => (
                    <tr key={`${a.fecha}-${a.matricula}-${a.flightTime}`}>
                      <td>{a.fecha?.slice(8, 10)}</td>
                      <td>{a.tipoAeronave || "—"}</td>
                      <td>{a.matricula || "—"}</td>
                      <td>
                        {a.rutaDesde || a.rutaHasta
                          ? `${a.rutaDesde || "—"} → ${a.rutaHasta || "—"}`
                          : "—"}
                      </td>
                      <td className="libro__num">{a.flightTimeHhMm || aHhMm(a.flightTime)}</td>
                      <td>
                        {etiquetaFuncion(a.funcion)}
                        {a.entrada && a.entrada !== "cronometro" && (
                          <span className="libro__entrada">
                            {a.libro || etiquetaDeEntrada(a)}
                          </span>
                        )}
                      </td>
                      <td className="libro__num">{a.ldgDia || 0}</td>
                      <td className="libro__num">{a.ldgNoche || 0}</td>

                      {/* ═══ CORREGIR Y QUITAR TAMBIÉN LOS VUELOS ═══

                          El dominio ya sabía corregir cualquier asiento, pero
                          la pantalla solo lo ofrecía en los arrastres. Un vuelo
                          cargado con la matrícula equivocada, o con una hora
                          mal tecleada, no se podía tocar.

                          Un libro que no se puede corregir obliga a llevar las
                          correcciones aparte, y ahí vuelve a haber dos
                          verdades. */}
                      {onReemplazarTodos && (
                        <td className="libro__acciones-fila">
                          <button
                            type="button"
                            className="libro__paso"
                            onClick={() => setEditando(a)}
                          >
                            Corregir
                          </button>
                          <button
                            type="button"
                            className="libro__quitar"
                            onClick={() => setPorQuitar(a)}
                            aria-label={`Quitar el vuelo del ${a.fecha}`}
                          >
                            ×
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}>Total del mes</td>
                    <td className="libro__num">{m.totales.totalHhMm}</td>
                    <td />
                    <td className="libro__num">{m.totales.ldgDia}</td>
                    <td className="libro__num">{m.totales.ldgNoche}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ currency */

/**
 * Estado de aterrizajes recientes.
 *
 * **No dice «habilitado».** Muestra el conteo contra el umbral configurado, y
 * mientras ese umbral no esté verificado contra la reglamentación del
 * operador, lo aclara. Un piloto que cree estar corriente y no lo está vuela
 * igual.
 */
function Currency({ currency }) {
  if (!currency?.valido) return null;

  const fila = (etiqueta, dato) => (
    <div className={`libro__currency-fila${dato.corriente ? " es-ok" : " es-falta"}`}>
      <span>{etiqueta}</span>
      <strong>{dato.hechos} / {dato.pide}</strong>
      {!dato.corriente && <small>faltan {dato.faltan}</small>}
    </div>
  );

  return (
    <div className="libro__currency">
      <span className="libro__currency-titulo">
        Aterrizajes en los últimos {currency.dias} días
      </span>

      {fila("Total", currency.dia)}
      {fila("De noche", currency.noche)}

      {/* La advertencia es permanente mientras el umbral no esté confirmado. */}
      {!currency.verificado && (
        <p className="libro__currency-aviso">
          Umbral no verificado. {currency.fuente} Este conteo <strong>no
          declara habilitación</strong>: confirma los valores que aplican a tu
          licencia y a tu operador.
        </p>
      )}

      {currency.sinFecha > 0 && (
        <p className="libro__currency-aviso">
          Hay {currency.sinFecha} asientos sin fecha que no se pudieron contar:
          el número real puede ser mayor.
        </p>
      )}
    </div>
  );
}


/* ------------------------------------------------------- alta en el libro */

/**
 * Carga a mano: un vuelo suelto o el arrastre de horas anteriores.
 *
 * Son dos cosas distintas y por eso el formulario cambia:
 *
 *   VUELO     tiene fecha, matrícula, función y aterrizajes. Cuenta para los
 *             períodos y para la currency.
 *   ARRASTRE  es un SALDO. No tiene matrícula ni función —es la suma de
 *             muchos vuelos— y NO cuenta para los períodos: contarlo diría que
 *             el piloto voló mil horas esta semana.
 */
function FormularioLibro({ tipo, pilotId, inicial = null, onGuardar, onGuardarDatos, onCancelar }) {
  const esArrastre = tipo === "arrastre";

  /* Al CORREGIR, los campos arrancan con lo que ya está guardado. Un
     formulario en blanco obligaría a transcribir todo de nuevo, que es
     justamente lo que se quiere evitar. */
  const desdeInicial = inicial
    ? {
      libro: inicial.libro || "",
      hasta: inicial.fecha || "",
      fecha: inicial.fecha || "",
      horasHhMm: inicial.flightTimeHhMm || (inicial.flightTime ? aHhMmDe(inicial.flightTime) : ""),
      flightTimeHhMm: inicial.flightTimeHhMm || (inicial.flightTime ? aHhMmDe(inicial.flightTime) : ""),
      matricula: inicial.matricula || "",
      tipoAeronave: inicial.tipoAeronave || "",
      rutaDesde: inicial.rutaDesde || "",
      rutaHasta: inicial.rutaHasta || "",
      funcion: inicial.funcion || "pic",
      condicion: inicial.condicion || "dia",
      observaciones: inicial.observaciones || "",
      ...Object.fromEntries(COLUMNAS_LIBRO.flatMap((c) => (
        c.tipo === "horas"
          ? [[`${c.clave}HhMm`, inicial[c.clave] ? aHhMmDe(inicial[c.clave]) : ""]]
          : [[c.clave, inicial[c.clave] != null ? String(inicial[c.clave]) : ""]]
      ))),
    }
    : null;

  const [campos, setCampos] = useState(desdeInicial || {
    fecha: new Date().toISOString().slice(0, 10),
    hasta: new Date().toISOString().slice(0, 10),
    flightTimeHhMm: "",
    horasHhMm: "",
    matricula: "",
    tipoAeronave: "",
    rutaDesde: "",
    rutaHasta: "",
    funcion: "pic",
    condicion: "dia",
    libro: "",
    observaciones: "",
    /* TODOS LOS DATOS EN CERO. Un campo vacío obliga a decidir si significa
       cero o «no lo sé»; el libro de papel arranca en cero y se llena. */
    ...Object.fromEntries(COLUMNAS_LIBRO.flatMap((c) => (
      c.tipo === "horas" ? [[`${c.clave}HhMm`, ""]]
        : [[c.clave, c.tipo === "entero" ? "0" : ""]]
    ))),
  });

  const [mensajes, setMensajes] = useState({ errores: [], avisos: [] });

  const set = (k, v) => setCampos((c) => ({ ...c, [k]: v }));

  const guardar = () => {
    const datos = { ...campos, pilotId };

    /* Antes de guardar, se comprueba QUÉ CAMPOS no se entendieron.

       Sin esto, una columna mal escrita se guardaba en cero sin decir nada: el
       piloto perdía las horas y solo lo notaba al mirar los totales, si las
       miraba. */
    const noEntendidos = COLUMNAS_LIBRO
      .filter((c) => c.tipo === "horas")
      .map((c) => ({ c, v: campos[`${c.clave}HhMm`] }))
      .filter(({ v }) => typeof v === "string" && v.trim() !== "" && deHhMm(v) === null)
      .map(({ c }) => `${c.etiqueta}: «${campos[`${c.clave}HhMm`]}»`);

    if (noEntendidos.length > 0) {
      setMensajes({
        errores: [`No se entienden estos valores como horas — ${noEntendidos.join(", ")}. `
          + "Escribe las horas (99) o las horas y minutos (99:30)."],
        avisos: [],
      });
      return;
    }
    /* Al corregir, quien decide es el libro: recibe los DATOS y arma el
       reemplazo conservando el identificador y el historial de correcciones. */
    if (onGuardarDatos) {
      const r = onGuardarDatos({ ...datos, horasHhMm: campos.horasHhMm });
      setMensajes({ errores: r?.errores || [], avisos: r?.avisos || [] });
      return;
    }

    const r = esArrastre
      ? asientoDeArrastre({ ...datos, horasHhMm: campos.horasHhMm })
      : asientoManual(datos);

    setMensajes({ errores: r.errores || [], avisos: r.avisos || [] });
    if (r.asiento) onGuardar(r.asiento);
  };

  const campo = (clave, etiqueta, tipoInput = "text", ayuda = null) => (
    <label className="libro__campo" key={clave}>
      <span>{etiqueta}</span>
      <input
        type={tipoInput}
        value={campos[clave] ?? ""}
        onChange={(e) => set(clave, e.target.value)}
        placeholder={clave.includes("HhMm") ? "HH:MM" : ""}
      />
      {ayuda && <small>{ayuda}</small>}
    </label>
  );

  return (
    <div className="libro__formulario" role="dialog">
      <h4 className="libro__form-titulo">
        {inicial ? "Corregir " : ""}
        {esArrastre ? "Horas anteriores al libro electrónico" : (inicial ? "vuelo" : "Cargar un vuelo")}
      </h4>

      {esArrastre ? (
        <>
          {/* El arrastre no es un vuelo: se dice antes de que lo cargue. */}
          <p className="libro__nota">
            El total de tu libro anterior, con <strong>todas sus columnas</strong>.
            Suma al total, pero <strong>no cuenta</strong> para los períodos ni
            para la currency: no se sabe cuándo se voló cada hora.
          </p>

          <div className="libro__form-campos">
            {campo("libro", "Qué libro", "text", "Por ejemplo: LIBRO ECUADOR")}
            {campo("horasHhMm", "Horas totales", "text", "En horas (1250) o en horas y minutos (1250:30)")}
            {campo("hasta", "Hasta la fecha", "date", "Hasta cuándo cubre este total")}
          </div>

          {/* LAS MISMAS COLUMNAS QUE UN VUELO.

              El libro no arrastra un total: arrastra la fila completa, con sus
              horas de PIC, de instrumentos y sus aterrizajes. Arrastrar solo el
              total dejaba el libro nuevo diciendo once mil horas y CERO como
              PIC — y para una licencia, ese desglose es lo que se mira. */}
          {GRUPOS_LIBRO.filter(([g]) => g !== "duracion").map(([grupo, nombre]) => (
            <fieldset className="libro__grupo" key={grupo}>
              <legend>{nombre}</legend>
              <div className="libro__form-campos">
                {COLUMNAS_LIBRO
                  .filter((c) => c.grupo === grupo && c.tipo !== "texto")
                  /* Dentro del grupo alcanza la etiqueta corta: el fieldset ya
                     dice si son aterrizajes o condiciones. */
                  .map((c) => campo(
                    c.tipo === "horas" ? `${c.clave}HhMm` : c.clave,
                    c.etiquetaCorta || c.etiqueta,
                    c.tipo === "entero" ? "number" : "text",
                    /* Las dos formas: un piloto escribe «99» tanto como
                       «99:00», y rechazar una de las dos perdía las horas en
                       silencio. */
                    c.tipo === "horas" ? "horas o HH:MM" : null,
                  ))}
              </div>
            </fieldset>
          ))}
        </>
      ) : (
        <>
          {/* Cabecera del asiento: lo que identifica el vuelo. */}
          <div className="libro__form-campos">
            {campo("fecha", "Fecha", "date")}
            {campo("tipoAeronave", "Marca y modelo")}
            {campo("matricula", "Matrícula")}
            {campo("rutaDesde", "Desde")}
            {campo("rutaHasta", "Hasta")}
            {campo("flightTimeHhMm", "Duración total", "text", "1:30 o 1.5 — desde el arranque")}
          </div>

          {/* Los grupos del libro de papel, con sus columnas.

              TODAS arrancan en CERO y se llenan solo las que aplican. Son
              columnas de desglose que se suman por separado, no categorías
              excluyentes: un vuelo de 2:45 puede tener 1:45 de instrumentos
              adentro. */}
          {GRUPOS_LIBRO.filter(([g]) => g !== "duracion").map(([grupo, nombre]) => (
            <fieldset className="libro__grupo" key={grupo}>
              <legend>{nombre}</legend>
              <div className="libro__form-campos">
                {COLUMNAS_LIBRO.filter((c) => c.grupo === grupo).map((c) => (
                  campo(
                    c.tipo === "horas" ? `${c.clave}HhMm` : c.clave,
                    c.etiquetaCorta || c.etiqueta,
                    c.tipo === "entero" ? "number" : "text",
                    c.clave === "ldgNoche"
                      ? "La habilitación nocturna se mantiene con estos"
                      : (c.tipo === "horas" ? "horas o HH:MM" : null),
                  )
                ))}
              </div>
            </fieldset>
          ))}

          <div className="libro__form-campos">
            {campo("observaciones", "Observaciones y endoso")}
          </div>
        </>
      )}

      {mensajes.errores.length > 0 && (
        <ul className="libro__errores" role="alert">
          {mensajes.errores.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
      {mensajes.avisos.length > 0 && (
        <p className="libro__nota">{mensajes.avisos.join(" ")}</p>
      )}

      <div className="libro__form-botones">
        <button type="button" className="libro__paso" onClick={onCancelar}>Cancelar</button>
        <button type="button" className="libro__exportar" onClick={guardar}>
          {inicial ? "Guardar corrección" : (esArrastre ? "Guardar arrastre" : "Guardar vuelo")}
        </button>
      </div>
    </div>
  );
}
